import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users, Search, Eye, Mail, Phone as PhoneIcon, MapPin,
  Calendar, ChevronLeft, ChevronRight, Megaphone, X, Copy, Hash,
  Activity, Sun, Moon, CheckCircle,
} from 'lucide-react';

import { useTheme } from '../components/Navbar';
import useAutoLogout from '../hooks/useAutoLogout';
import Toast from '../components/Toast';
import API_BASE from '../config';

const TK = {
  light: {
    pageBg:'#F5F6FA', card:'#FFFFFF', border:'#ECEEF2',
    text:'#0F1623', sub:'#6B7280', muted:'#9CA3AF',
    accent:'#5B4EE8', accentBg:'#EEF0FD', accentTxt:'#4338CA',
    row:'#FAFBFC', rowBdr:'#F0F2F5', input:'#FFFFFF',
    btnPri:'#5B4EE8', btnPriTxt:'#fff',
    chip:'#F3F4F6', chipTxt:'#374151', overlay:'rgba(15,22,35,0.32)',
    toggleBg:'#ECEEF2', toggleActive:'#fff',
    avatar:'linear-gradient(135deg,#6C63FF,#5B4EE8)',
    head:'#FAFBFC',
  },
  dark: {
    pageBg:'#0D0D14', card:'#131320', border:'#1E1E2E',
    text:'#F1F2F6', sub:'#8B8FA8', muted:'#4B5263',
    accent:'#7C72FF', accentBg:'rgba(124,114,255,0.12)', accentTxt:'#A5A0FF',
    row:'#18182A', rowBdr:'#1E1E2E', input:'#18182A',
    btnPri:'#6C63FF', btnPriTxt:'#fff',
    chip:'#1A1A2A', chipTxt:'#C7CBDA', overlay:'rgba(0,0,0,0.55)',
    toggleBg:'#1A1A2A', toggleActive:'#1E1E2E',
    avatar:'linear-gradient(135deg,#7C72FF,#5B4EE8)',
    head:'#15152340',
  },
};

const TENANT_META = {
  hospital:   { label:'Hospital',   color:'#10B981' },
  restaurant: { label:'Restaurant', color:'#F59E0B' },
};
const WA_GREEN = '#22C55E';
const PAGE_SIZE = 10;

function tenantMeta(tenant) {
  const t = tenant || '';
  if (t.includes('hospital'))   return TENANT_META.hospital;
  if (t.includes('restaurant')) return TENANT_META.restaurant;
  return { label: tenant || '—', color: '#9CA3AF' };
}

function relativeTime(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hrs  = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1)  return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs  < 24) return `${hrs}h ago`;
  return `${days}d ago`;
}

function toIST(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const utc = d.getTime() + (d.getTimezoneOffset() * 60000);
  const ist = new Date(utc + (5.5 * 3600000));
  return ist.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
}
const formatDate = toIST;

function initials(first, last) {
  return `${(first || '?')[0]}${(last || '')[0] || ''}`.toUpperCase();
}
const isWaOptIn = (val) => val === true || val === 't' || val === 'true' || val === 1;

function ThemeToggle({ theme, setTheme, tk }) {
  return (
    <div className="flex items-center gap-1 p-1 rounded-xl flex-shrink-0" style={{ background: tk.toggleBg }}>
      {[{ key:'light', Icon: Sun }, { key:'dark', Icon: Moon }].map(({ key, Icon }) => {
        const active = theme === key;
        return (
          <button key={key} onClick={() => setTheme(key)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize"
            style={{ background: active ? tk.toggleActive : 'transparent', color: active ? tk.text : tk.sub,
              boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none' }}>
            <Icon size={13} />{key}
          </button>
        );
      })}
    </div>
  );
}

function SkeletonRow({ tk }) {
  return (
    <tr style={{ borderBottom: `1px solid ${tk.rowBdr}` }}>
      {[36,120,90,140,90,80,90,80,90,80,36,30].map((w,i) => (
        <td key={i} className="px-4 py-4"><div className="shimmer h-4 rounded" style={{ width: w }} /></td>
      ))}
    </tr>
  );
}

function ProfileDrawer({ profile, onClose, token, onToast, tk }) {
  const navigate = useNavigate();
  const [recentEvents, setRecentEvents] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sendingEmail,  setSendingEmail]  = useState(false);

  useEffect(() => {
    if (!profile) return;
    setDetailLoading(true);
    fetch(`${API_BASE}/api/v1/profiles/${profile.profile_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (d.success) setRecentEvents(d.recent_events || []); })
      .catch(() => {})
      .finally(() => setDetailLoading(false));
  }, [profile, token]);

  if (!profile) return null;

  const attrs    = profile.attributes || {};
  const hasAttrs = Object.keys(attrs).length > 0;
  const tm       = tenantMeta(profile.tenant_id);
  const waOn     = isWaOptIn(profile.whatsapp_opted_in);

  const copyProfileId = () =>
    navigator.clipboard.writeText(profile.profile_id || '')
      .then(() => onToast('Copied!', 'green'))
      .catch(() => onToast('Copy failed', 'red'));

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      await fetch(`${API_BASE}/api/v1/email/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile_id: profile.profile_id, dry_run: true }),
      });
    } catch { /* no-op */ }
    onToast('Email queued (dry run)', 'blue');
    setSendingEmail(false);
  };

  const pill = (on, label, offLabel) => (
    <span className="text-[11px] font-bold px-3 py-1.5 rounded-full"
      style={{ background: on ? 'rgba(34,197,94,0.12)' : tk.chip,
               color: on ? WA_GREEN : tk.muted,
               border: `1px solid ${on ? 'rgba(34,197,94,0.25)' : tk.border}` }}>
      {on ? label : offLabel}
    </span>
  );

  return (
    <>
      <div className="fixed inset-0 z-[100] backdrop-blur-sm" style={{ background: tk.overlay }} onClick={onClose} />
      <div className="fixed top-0 right-0 h-full z-[110] w-full max-w-sm flex flex-col"
        style={{ background: tk.card, borderLeft: `1px solid ${tk.border}` }}>
        <div className="px-6 py-5 flex-shrink-0" style={{ borderBottom: `1px solid ${tk.border}` }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-black" style={{ color: tk.text }}>Profile Detail</h2>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl transition-all"
              style={{ color: tk.sub }}
              onMouseEnter={e => { e.currentTarget.style.background = tk.row; e.currentTarget.style.color = tk.text; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = tk.sub; }}>
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-black text-xl flex-shrink-0"
              style={{ background: tk.avatar }}>{initials(profile.first_name, profile.last_name)}</div>
            <div>
              <p className="font-black text-lg leading-tight" style={{ color: tk.text }}>
                {[profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Anonymous'}
              </p>
              <span className="inline-block mt-1.5 text-[11px] font-bold px-2.5 py-0.5 rounded-full"
                style={{ background: `${tm.color}1A`, color: tm.color, border: `1px solid ${tm.color}33` }}>
                {tm.label}
              </span>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: tk.muted }}>Contact</p>
            <div className="space-y-2.5">
              {profile.profile_id && (
                <div className="flex items-center gap-3">
                  <Hash className="w-4 h-4 flex-shrink-0" style={{ color: tk.muted }} />
                  <div className="flex items-center gap-1.5 flex-1 min-w-0">
                    <span className="text-xs font-mono truncate" style={{ color: tk.sub }}>{profile.profile_id}</span>
                    <button onClick={copyProfileId}
                      className="w-5 h-5 flex items-center justify-center rounded flex-shrink-0 transition-all"
                      style={{ color: tk.muted }}
                      onMouseEnter={e => { e.currentTarget.style.color = tk.accent; e.currentTarget.style.background = tk.accentBg; }}
                      onMouseLeave={e => { e.currentTarget.style.color = tk.muted; e.currentTarget.style.background = 'transparent'; }}>
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
              {profile.created_at && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 flex-shrink-0" style={{ color: tk.accent }} />
                  <div>
                    <span className="text-sm" style={{ color: tk.text }}>{formatDate(profile.created_at)}</span>
                    <span className="text-xs ml-2" style={{ color: tk.muted }}>Opted in</span>
                  </div>
                </div>
              )}
              {profile.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 flex-shrink-0" style={{ color: tk.muted }} />
                  <span className="text-sm truncate" style={{ color: tk.text }}>{profile.email}</span>
                </div>
              )}
              {profile.phone && (
                <div className="flex items-center gap-3">
                  <PhoneIcon className="w-4 h-4 flex-shrink-0" style={{ color: tk.muted }} />
                  <span className="text-sm" style={{ color: tk.text }}>{profile.phone}</span>
                </div>
              )}
              {profile.city && (
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: tk.muted }} />
                  <span className="text-sm" style={{ color: tk.text }}>{profile.city}</span>
                </div>
              )}
              {profile.dob && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 flex-shrink-0" style={{ color: tk.muted }} />
                  <span className="text-sm" style={{ color: tk.text }}>
                    {new Date(profile.dob).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: tk.muted }}>Engagement</p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rounded-xl p-3" style={{ background: tk.row, border: `1px solid ${tk.border}` }}>
                <p className="text-xl font-black" style={{ color: tk.text }}>{profile.visit_count || 0}</p>
                <p className="text-xs mt-0.5" style={{ color: tk.muted }}>Visits</p>
              </div>
              <div className="rounded-xl p-3" style={{ background: tk.row, border: `1px solid ${tk.border}` }}>
                <p className="text-sm font-bold truncate" style={{ color: tk.text }}>{relativeTime(profile.last_seen)}</p>
                <p className="text-xs mt-0.5" style={{ color: tk.muted }}>Last seen</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {pill(profile.age_verified, '✓ Age Verified', 'Age Unverified')}
              {pill(waOn, '✓ WhatsApp', 'No WhatsApp')}
            </div>
          </div>

          {hasAttrs && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: tk.muted }}>Attributes</p>
              <div className="rounded-xl overflow-hidden" style={{ background: tk.row, border: `1px solid ${tk.border}` }}>
                {Object.entries(attrs).map(([k, v], i, arr) => (
                  <div key={k} className="flex items-center justify-between px-4 py-2.5"
                    style={{ borderBottom: i < arr.length - 1 ? `1px solid ${tk.rowBdr}` : 'none' }}>
                    <span className="text-xs capitalize" style={{ color: tk.sub }}>{k.replace(/_/g, ' ')}</span>
                    <span className="text-xs font-medium" style={{ color: tk.text }}>{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: tk.muted }}>Recent Activity</p>
            {detailLoading ? (
              <div className="shimmer h-16 rounded-xl" />
            ) : recentEvents.length > 0 ? (
              <div className="space-y-2">
                {recentEvents.slice(0, 6).map((ev, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 rounded-xl"
                    style={{ background: tk.row, border: `1px solid ${tk.border}` }}>
                    <span className="text-xs truncate" style={{ color: tk.text }}>{ev.event_name || 'Event'}</span>
                    <span className="text-xs ml-2 flex-shrink-0" style={{ color: tk.muted }}>{relativeTime(ev.event_timestamp)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center rounded-xl" style={{ background: tk.row, border: `1px solid ${tk.border}` }}>
                <Activity className="w-6 h-6 mx-auto mb-1" style={{ color: tk.muted }} />
                <p className="text-xs" style={{ color: tk.muted }}>No recent activity</p>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 pb-6 pt-4 space-y-3 flex-shrink-0" style={{ borderTop: `1px solid ${tk.border}` }}>
          <button onClick={handleSendEmail} disabled={sendingEmail}
            className="w-full py-3 rounded-xl text-sm font-bold transition-all hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: tk.chip, color: tk.text, border: `1px solid ${tk.border}` }}>
            <Mail className="w-4 h-4" />
            {sendingEmail ? 'Sending…' : 'Send Email (Dry Run)'}
          </button>
          <button onClick={() => { navigate('/campaigns'); onClose(); }}
            className="w-full py-3 rounded-xl text-sm font-bold transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
            style={{ background: tk.btnPri, color: tk.btnPriTxt }}>
            <Megaphone className="w-4 h-4" />
            Schedule Campaign
          </button>
        </div>
      </div>
    </>
  );
}

export default function Profiles() {
  const navigate = useNavigate();
  useAutoLogout();
  const { theme, setTheme } = useTheme();
  const tk = TK[theme] || TK.light;
  const [searchParams] = useSearchParams();

  const [profiles, setProfiles]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [filter, setFilter]           = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [waFilter, setWaFilter]       = useState(false);
  const [page, setPage]               = useState(1);
  const [selected, setSelected]       = useState(null);
  const [toast, setToast]             = useState(null);

  const token =
    localStorage.getItem('martech_token') ||
    sessionStorage.getItem('martech_token') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token');

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    fetch(`${API_BASE}/api/v1/profiles?limit=1000`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then(r => r.json())
      .then(d => { if (d.success) setProfiles(d.profiles || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate, token]);

  useEffect(() => {
    const seg = searchParams.get('segment');
    if (seg) setFilter(seg);
  }, [searchParams]);

  const showToast = (msg, variant = 'green') => {
    setToast({ msg, variant });
    setTimeout(() => setToast(null), 3100);
  };

  const segmentTypes = [...new Set(profiles.map(p => p.segment_type).filter(Boolean))].sort();

  const filtered = profiles.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (p.first_name || '').toLowerCase().includes(q) ||
      (p.last_name  || '').toLowerCase().includes(q) ||
      (p.email      || '').toLowerCase().includes(q) ||
      (p.phone      || '').includes(q);
    const matchTenant = filter === 'all' || p.tenant_id === filter;
    const matchType   = selectedType === 'all' || p.segment_type === selectedType;
    const matchWa     = !waFilter || isWaOptIn(p.whatsapp_opted_in);
    return matchSearch && matchTenant && matchType && matchWa;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const whatsappCount    = profiles.filter(p => isWaOptIn(p.whatsapp_opted_in)).length;
  const ageVerifiedCount = profiles.filter(p => p.age_verified).length;
  const hospitalCount    = profiles.filter(p => (p.tenant_id || '').includes('hospital')).length;
  const restaurantCount  = profiles.filter(p => (p.tenant_id || '').includes('restaurant')).length;

  const handleSearch = v => { setSearch(v); setPage(1); };
  const handleFilter = v => { setFilter(v); setPage(1); };
  const handleType   = v => { setSelectedType(v); setPage(1); };
  const handleWa     = v => { setWaFilter(v); setPage(1); };

  const copyProfileId = (id) =>
    navigator.clipboard.writeText(id || '')
      .then(() => showToast('Copied!', 'green'))
      .catch(() => showToast('Copy failed', 'red'));

  const inputStyle = { background: tk.input, border: `1px solid ${tk.border}`, color: tk.text };

  const stats = [
    { label: 'Total Profiles',       value: profiles.length,                         color: tk.text,   dot: tk.accent  },
    { label: 'WhatsApp Opted In',     value: whatsappCount,                           color: WA_GREEN,  dot: WA_GREEN   },
    { label: 'Age Verified',          value: ageVerifiedCount,                        color: '#3B82F6', dot: '#3B82F6'  },
    { label: 'Hospital / Restaurant', value: `${hospitalCount} / ${restaurantCount}`, color: '#F59E0B', dot: '#F59E0B'  },
  ];

  return (
    <div className="min-h-screen" style={{ background: tk.pageBg }}>
      <div className="h-14 md:hidden" />

      {selected && (
        <ProfileDrawer profile={selected} onClose={() => setSelected(null)}
          token={token} onToast={showToast} tk={tk} />
      )}
      {toast && <Toast message={toast.msg} variant={toast.variant} onDismiss={() => setToast(null)} />}

      <main className="px-4 md:px-5 py-6 pt-20 md:pt-8 max-w-[1500px] mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-5">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: tk.accentBg }}>
              <Users className="w-5 h-5" style={{ color: tk.accent }} />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl md:text-3xl font-black tracking-tight" style={{ color: tk.text }}>Profiles</h1>
                {!loading && (
                  <span className="px-3 py-1 text-sm font-black rounded-full"
                    style={{ background: tk.accentBg, color: tk.accentTxt }}>{profiles.length}</span>
                )}
              </div>
              <p className="text-sm mt-1" style={{ color: tk.sub }}>
                {loading ? 'Loading profiles…' : `Showing ${filtered.length} of ${profiles.length} profiles`}
              </p>
            </div>
          </div>
          <ThemeToggle theme={theme} setTheme={setTheme} tk={tk} />
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-5">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: tk.muted }} />
            <input type="text" value={search} onChange={e => handleSearch(e.target.value)}
              placeholder="Search name / email / phone…"
              className="pl-10 pr-4 py-2.5 rounded-xl text-sm outline-none w-full transition-colors"
              style={inputStyle}
              onFocus={e => { e.target.style.borderColor = tk.accent; }}
              onBlur={e  => { e.target.style.borderColor = tk.border; }} />
          </div>
          <div className="flex flex-wrap gap-3">
            <select value={filter} onChange={e => handleFilter(e.target.value)}
              className="px-4 py-2.5 rounded-xl text-sm outline-none flex-1 sm:flex-none"
              style={{ ...inputStyle, appearance: 'none', paddingRight: 32 }}>
              <option value="all">All Tenants</option>
              <option value="hospital_001">Hospital</option>
              <option value="restaurant_001">Restaurant</option>
            </select>
            {segmentTypes.length > 0 && (
              <select value={selectedType} onChange={e => handleType(e.target.value)}
                className="px-4 py-2.5 rounded-xl text-sm outline-none flex-1 sm:flex-none"
                style={{ ...inputStyle, appearance: 'none', paddingRight: 32 }}>
                <option value="all">All Segments</option>
                {segmentTypes.map(t => (
                  <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                ))}
              </select>
            )}
            <label className="flex items-center gap-2 cursor-pointer select-none self-center">
              <div className="relative w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-all duration-150"
                style={{ background: waFilter ? tk.accent : tk.input, border: `1px solid ${waFilter ? tk.accent : tk.border}` }}>
                <input type="checkbox" checked={waFilter} onChange={e => handleWa(e.target.checked)}
                  className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                {waFilter && (
                  <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <span className="text-sm whitespace-nowrap" style={{ color: tk.sub }}>WhatsApp opted-in</span>
            </label>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {stats.map(({ label, value, color, dot }) => (
            <div key={label} className="rounded-2xl p-4" style={{ background: tk.card, border: `1px solid ${tk.border}` }}>
              <div className="w-8 h-8 rounded-xl mb-3 flex items-center justify-center" style={{ background: `${dot}1F` }}>
                <div className="w-2 h-2 rounded-full" style={{ background: dot }} />
              </div>
              {loading
                ? <div className="shimmer h-8 w-16 mb-1 rounded" />
                : <p className="text-2xl font-black" style={{ color }}>{value}</p>}
              <p className="text-xs mt-1" style={{ color: tk.sub }}>{label}</p>
            </div>
          ))}
        </div>

        {/* ── MOBILE card list (hidden on md+) ── */}
        <div className="md:hidden space-y-3 mb-5">
          {loading ? (
            Array(5).fill(null).map((_, i) => (
              <div key={i} className="shimmer h-28 rounded-2xl" />
            ))
          ) : paginated.length === 0 ? (
            <div className="py-14 text-center rounded-2xl" style={{ background: tk.card, border: `1px solid ${tk.border}` }}>
              <Users className="w-8 h-8 mx-auto mb-2" style={{ color: tk.muted }} />
              <p className="text-sm" style={{ color: tk.sub }}>No profiles found</p>
            </div>
          ) : (
            paginated.map((p, idx) => {
              const tm = tenantMeta(p.tenant_id);
              const waOn = isWaOptIn(p.whatsapp_opted_in);
              return (
                <div key={p.profile_id || idx} className="rounded-2xl p-4"
                  style={{ background: tk.card, border: `1px solid ${tk.border}` }}>
                  {/* Avatar + name + eye */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-xs flex-shrink-0"
                      style={{ background: tk.avatar }}>
                      {initials(p.first_name, p.last_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate" style={{ color: tk.text }}>
                        {[p.first_name, p.last_name].filter(Boolean).join(' ') || 'Anonymous'}
                      </p>
                      <span className="inline-block mt-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{ background: `${tm.color}1A`, color: tm.color, border: `1px solid ${tm.color}33` }}>
                        {tm.label}
                      </span>
                    </div>
                    <button onClick={() => setSelected(p)}
                      className="w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0"
                      style={{ background: tk.accentBg, color: tk.accent }}>
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Contact */}
                  <div className="space-y-1.5 mb-3">
                    {p.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 flex-shrink-0" style={{ color: tk.muted }} />
                        <span className="text-xs truncate" style={{ color: tk.sub }}>{p.email}</span>
                      </div>
                    )}
                    {p.phone && (
                      <div className="flex items-center gap-2">
                        <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: tk.muted }} />
                        <span className="text-xs" style={{ color: tk.sub }}>{p.phone}</span>
                      </div>
                    )}
                  </div>
                  {/* Tags row */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.segment_type && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                        style={{ background: tk.chip, color: tk.sub }}>
                        {p.segment_type.replace(/_/g, ' ')}
                      </span>
                    )}
                    {p.city && <span className="text-[10px]" style={{ color: tk.muted }}>{p.city}</span>}
                    <span className="text-[10px]" style={{ color: tk.muted }}>{relativeTime(p.last_seen)}</span>
                    {waOn && <CheckCircle className="w-3.5 h-3.5" style={{ color: WA_GREEN }} />}
                    <span className="text-[10px] font-black ml-auto" style={{ color: tk.accent }}>
                      {p.visit_count || 0} visits
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ── DESKTOP table (hidden on mobile) ── */}
        <div className="hidden md:block rounded-2xl overflow-hidden mb-5"
          style={{ background: tk.card, border: `1px solid ${tk.border}` }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: 1080 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${tk.border}` }}>
                  {['','Name','Profile ID','Email','Phone','Tenant','Segment','City','Opted In','Last Seen','Visits','WA',''].map((col, i) => (
                    <th key={i} className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-left"
                      style={{ color: tk.muted, background: tk.head, position: 'sticky', top: 0 }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array(8).fill(null).map((_, i) => <SkeletonRow key={i} tk={tk} />)
                ) : paginated.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="py-20 text-center">
                      <Users className="w-10 h-10 mx-auto mb-3" style={{ color: tk.muted }} />
                      <p className="text-sm" style={{ color: tk.sub }}>No profiles found</p>
                    </td>
                  </tr>
                ) : (
                  paginated.map((p, idx) => {
                    const tm = tenantMeta(p.tenant_id);
                    return (
                      <tr key={p.profile_id || idx} className="transition-colors"
                        style={{ borderBottom: `1px solid ${tk.rowBdr}` }}
                        onMouseEnter={e => { e.currentTarget.style.background = tk.row; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                        <td className="px-4 py-3">
                          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-black text-xs flex-shrink-0"
                            style={{ background: tk.avatar }}>{initials(p.first_name, p.last_name)}</div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-sm whitespace-nowrap" style={{ color: tk.text }}>
                            {[p.first_name, p.last_name].filter(Boolean).join(' ') || 'Anonymous'}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-mono" style={{ color: tk.sub }}>
                              {p.profile_id ? p.profile_id.slice(0, 8) : '—'}
                            </span>
                            {p.profile_id && (
                              <button onClick={() => copyProfileId(p.profile_id)}
                                className="w-5 h-5 flex items-center justify-center rounded transition-all"
                                style={{ color: tk.muted }}
                                onMouseEnter={e => { e.currentTarget.style.color = tk.accent; e.currentTarget.style.background = tk.accentBg; }}
                                onMouseLeave={e => { e.currentTarget.style.color = tk.muted; e.currentTarget.style.background = 'transparent'; }}>
                                <Copy className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: tk.sub, maxWidth: 160 }}>
                          <span className="block truncate">{p.email || '—'}</span>
                        </td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: tk.sub }}>{p.phone || '—'}</td>
                        <td className="px-4 py-3">
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap"
                            style={{ background: `${tm.color}1A`, color: tm.color, border: `1px solid ${tm.color}33` }}>
                            {tm.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: tk.sub, maxWidth: 110 }}>
                          <span className="block truncate">{(p.segment_type || '—').replace(/_/g, ' ')}</span>
                        </td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: tk.sub }}>{p.city || '—'}</td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: tk.sub }}>{formatDate(p.created_at)}</td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: tk.sub }}>{relativeTime(p.last_seen)}</td>
                        <td className="px-4 py-3 font-black text-sm" style={{ color: tk.accent }}>{p.visit_count || 0}</td>
                        <td className="px-4 py-3">
                          {isWaOptIn(p.whatsapp_opted_in)
                            ? <CheckCircle className="w-4 h-4" style={{ color: WA_GREEN }} />
                            : <span className="text-sm" style={{ color: tk.muted }}>—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => setSelected(p)}
                            className="w-8 h-8 flex items-center justify-center rounded-xl transition-all"
                            style={{ color: tk.sub }}
                            onMouseEnter={e => { e.currentTarget.style.color = tk.accent; e.currentTarget.style.background = tk.accentBg; }}
                            onMouseLeave={e => { e.currentTarget.style.color = tk.sub; e.currentTarget.style.background = 'transparent'; }}>
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination — works for both mobile cards and desktop table */}
        {!loading && filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between">
            <p className="text-xs" style={{ color: tk.muted }}>
              {((page-1)*PAGE_SIZE)+1}–{Math.min(page*PAGE_SIZE, filtered.length)} of {filtered.length}
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
                className="w-8 h-8 flex items-center justify-center rounded-xl transition-colors disabled:opacity-30"
                style={{ background: tk.card, border: `1px solid ${tk.border}`, color: tk.sub }}>
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-semibold px-2" style={{ color: tk.text }}>{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-xl transition-colors disabled:opacity-30"
                style={{ background: tk.card, border: `1px solid ${tk.border}`, color: tk.sub }}>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}